import { useEffect, useState } from 'react';
import {
  api,
  type LgwGateway,
  type WxcLocalGateway,
  type CubeDialPeer,
  type CubeSipTrunk,
  type CubeActiveCall,
  type CubeDeviceCert,
} from '../lib/api';

// ---------------------------------------------------------------------------
// Merged Local Gateway view — ONE card per gateway showing both sides:
//   • Webex Control Hub (cloud trunk)  — wxc-svc /api/wxc/local-gateways
//   • Device RESTCONF (on-box truth)   — lgw-svc cube_* (via cube-tunnel-svc)
// The two are fetched independently and joined by gateway identity, so the
// card degrades gracefully: Webex shows now, device fills in once the
// cube-lb tunnel is live (until then it shows "awaiting tunnel").
// ---------------------------------------------------------------------------

type Bundle = {
  gateways: LgwGateway[];
  webex: WxcLocalGateway[];
  dialPeers: CubeDialPeer[];
  sipTrunks: CubeSipTrunk[];
  activeCalls: CubeActiveCall[];
  certs: CubeDeviceCert[];
  webexError?: boolean;
  deviceError?: boolean;
};

function pill(tone: 'ok' | 'warn' | 'err' | 'idle', label: string) {
  const cls =
    tone === 'ok' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
    : tone === 'warn' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
    : tone === 'err' ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200'
    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
  const dot =
    tone === 'ok' ? 'bg-emerald-500' : tone === 'warn' ? 'bg-amber-500' : tone === 'err' ? 'bg-rose-500' : 'bg-gray-400';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

function ago(ms: number | null | undefined) {
  if (!ms) return '—';
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5">
      <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{k}</span>
      <span className="text-sm text-right font-medium tabular-nums">{v}</span>
    </div>
  );
}

// Best-effort join: a Webex premise-PSTN trunk to an on-prem gateway.
function matchWebex(gw: LgwGateway, trunks: WxcLocalGateway[]): WxcLocalGateway | undefined {
  const host = (gw.host || '').toLowerCase();
  const name = (gw.name || '').toLowerCase();
  return trunks.find((t) => {
    const op = (t.outbound_proxy || '').toLowerCase();
    const tn = (t.name || '').toLowerCase();
    return (!!host && op.includes(host)) || (!!name && (tn.includes(name) || op.includes(name)));
  });
}

export function LgwMergedCard() {
  const [b, setB] = useState<Bundle | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    setErr(null);
    const [gw, wx, dp, st, ac, ce] = await Promise.allSettled([
      api.lgwGateways(),
      api.wxcLocalGateways(),
      api.lgwDialPeers(),
      api.lgwSipTrunks(),
      api.lgwActiveCalls(),
      api.lgwCubeCerts(),
    ]);
    if (gw.status !== 'fulfilled') {
      setErr('Could not load gateways from lgw-svc.');
      setB(null);
      setLoading(false);
      return;
    }
    setB({
      gateways: gw.value.gateways ?? [],
      webex: wx.status === 'fulfilled' ? wx.value.local_gateways ?? [] : [],
      webexError: wx.status !== 'fulfilled',
      dialPeers: dp.status === 'fulfilled' ? dp.value.dial_peers ?? [] : [],
      sipTrunks: st.status === 'fulfilled' ? st.value.sip_trunks ?? [] : [],
      activeCalls: ac.status === 'fulfilled' ? ac.value.active_calls ?? [] : [],
      certs: ce.status === 'fulfilled' ? (ce.value as any).certs ?? [] : [],
      deviceError: dp.status !== 'fulfilled',
    });
    setLoading(false);
  }

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, []);

  if (loading && !b) {
    return <div className="card p-8 text-center text-sm text-gray-500">Loading Local Gateway view…</div>;
  }
  if (err) {
    return <div className="card p-4 text-sm text-rose-700 dark:text-rose-300">{err}</div>;
  }
  if (!b || b.gateways.length === 0) {
    return (
      <div className="card p-6 text-sm text-gray-500">
        No Local Gateways registered for this tenant yet.
      </div>
    );
  }

  const matchedWebexIds = new Set<string>();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-base font-semibold">Local Gateway — Merged View</h3>
          <p className="text-xs text-gray-500">
            Webex Control Hub trunk status + on-box RESTCONF device detail, one card per gateway.
          </p>
        </div>
        <button onClick={load} className="btn-ghost text-sm" disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {b.gateways.map((gw) => {
        const wx = matchWebex(gw, b.webex);
        if (wx) matchedWebexIds.add(wx.id);
        const reachable = gw.status === 'reachable';

        const dps = b.dialPeers.filter((d) => d.gateway_id === gw.gateway_id);
        const trunks = b.sipTrunks.filter((s) => s.gateway_id === gw.gateway_id);
        const reg = trunks.filter((s) => s.registration_state === 'registered').length;
        const calls = b.activeCalls.filter((c) => c.gateway_id === gw.gateway_id);
        const certs = b.certs.filter((c) => c.gateway_id === gw.gateway_id);
        const worstCert = certs.reduce<CubeDeviceCert | null>((w, c) => {
          if (c.days_until_expiry == null) return w;
          if (!w || w.days_until_expiry == null) return c;
          return c.days_until_expiry < w.days_until_expiry ? c : w;
        }, null);

        // overall health: combine the two sides
        const webexOk = wx ? (wx.device_status === 'online' || (wx.in_use && (wx.error_codes?.length ?? 0) === 0)) : false;
        const overall: 'ok' | 'warn' | 'err' | 'idle' =
          reachable && webexOk ? 'ok'
          : reachable || webexOk ? 'warn'
          : 'idle';

        return (
          <div key={gw.gateway_id} className="card p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{gw.name}</span>
                <span className="text-xs text-gray-500 font-mono">{gw.host}</span>
                <span className="pill bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-[11px]">{gw.role}</span>
              </div>
              {pill(overall, overall === 'ok' ? 'Healthy' : overall === 'warn' ? 'Partial' : 'Awaiting data')}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Webex / cloud side */}
              <div className="rounded-lg border border-violet-200 dark:border-violet-900/40 p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-violet-700 dark:text-violet-300">
                    Webex Control Hub (cloud)
                  </span>
                </div>
                {b.webexError ? (
                  <div className="text-xs text-gray-500">Webex Calling data unavailable (check wxc-svc credentials).</div>
                ) : wx ? (
                  <div className="space-y-0.5">
                    <Row k="Trunk" v={wx.name} />
                    <Row k="Location" v={wx.location_name || '—'} />
                    <Row k="Type" v={wx.trunk_type || '—'} />
                    <Row k="Status" v={pill(
                      wx.device_status === 'online' ? 'ok' : (wx.error_codes?.length ?? 0) > 0 ? 'err' : 'idle',
                      wx.device_status || 'unknown',
                    )} />
                    <Row k="In use" v={wx.in_use ? 'Yes' : 'No'} />
                    <Row k="Max concurrent" v={wx.max_concurrent_calls ?? '—'} />
                    <Row k="Outbound proxy" v={<span className="font-mono text-xs">{wx.outbound_proxy || '—'}</span>} />
                    {(wx.error_codes?.length ?? 0) > 0 && (
                      <Row k="Errors" v={<span className="text-rose-600">{wx.error_codes!.join(', ')}</span>} />
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">No matching Webex trunk found for this gateway.</div>
                )}
              </div>

              {/* Device / RESTCONF side */}
              <div className="rounded-lg border border-indigo-200 dark:border-indigo-900/40 p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-indigo-700 dark:text-indigo-300">
                    Device · RESTCONF (on-box)
                  </span>
                </div>
                {reachable ? (
                  <div className="space-y-0.5">
                    <Row k="Reachability" v={pill('ok', 'reachable')} />
                    <Row k="IOS-XE" v={gw.ios_version || '—'} />
                    <Row k="Last sync" v={ago(gw.last_seen_at)} />
                    <Row k="Dial peers" v={dps.length} />
                    <Row k="SIP trunks" v={`${reg}/${trunks.length} registered`} />
                    <Row k="Active calls" v={calls.length} />
                    <Row k="Certs" v={
                      worstCert
                        ? pill(
                            worstCert.status_live === 'expired' || worstCert.status_live === 'critical' ? 'err'
                            : worstCert.status_live === 'expiring' ? 'warn' : 'ok',
                            worstCert.days_until_expiry != null ? `${worstCert.days_until_expiry}d to expiry` : worstCert.status_live,
                          )
                        : `${certs.length} on device`
                    } />
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 space-y-1">
                    <div>{pill('idle', gw.status)}</div>
                    <p>
                      Awaiting the <span className="font-mono">cube-lb</span> tunnel. Once the public hostname + Access +
                      secrets are set and <span className="font-mono">cube-tunnel-svc</span> syncs, dial-peers, SIP-trunk
                      registration, live calls and certificates appear here.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Webex trunks that didn't map to any registered gateway */}
      {(() => {
        const orphans = b.webex.filter((t) => !matchedWebexIds.has(t.id));
        if (orphans.length === 0) return null;
        return (
          <div className="card p-3">
            <div className="text-xs font-semibold text-gray-500 mb-2">
              Webex trunks with no matching on-prem gateway ({orphans.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {orphans.map((t) => (
                <span key={t.id} className="inline-flex items-center gap-2 text-xs border border-tnh-border dark:border-tnh-border-dark rounded-lg px-2.5 py-1">
                  <span className="font-medium">{t.name}</span>
                  <span className="text-gray-500">{t.location_name}</span>
                  {pill(t.device_status === 'online' ? 'ok' : 'idle', t.device_status || 'unknown')}
                </span>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
